/**
 * Utilidades para pintar contenido de correo dentro de un iframe aislado.
 *
 * Se comparten entre el visor del correo y la previsualizacion de adjuntos para
 * no repetir la politica de seguridad ni el armado del documento html.
 */

//Politica que bloquea el contenido remoto del correo (rastreadores y scripts)
export const CSP_BLOCKED = "default-src 'none'; img-src blob: data:; style-src 'unsafe-inline'; font-src data:;";
//Politica que permite las imagenes remotas cuando el usuario lo autoriza
export const CSP_ALLOWED =
  "default-src 'none'; img-src blob: data: https: http:; style-src 'unsafe-inline'; font-src data:;";

/**
 * Aislamiento del cuerpo del correo.
 * Sin allow-scripts no se ejecuta javascript del correo (el html llega de un
 * tercero), pero se necesita allow-same-origin para que las imagenes cid:
 * convertidas a blob: del propio origen se puedan cargar dentro del iframe.
 * Los permisos de popup dejan que los enlaces del correo abran en otra pestana
 * fuera del aislamiento, tal como lo hace un cliente de correo.
 */
export const SANDBOX = "allow-same-origin allow-popups allow-popups-to-escape-sandbox";

const FRAME_STYLE =
  'body{margin:0;padding:16px;font-family:Segoe UI,Arial,sans-serif;font-size:14px;color:#212529;}' +
  'img{max-width:100%;height:auto;}table{max-width:100%;}pre{white-space:pre-wrap;word-break:break-word;}';

/**
 * Escapa los caracteres que el navegador interpretaria como marcado
 * @param {string} value texto original
 * @returns {string} texto seguro para incrustar en html
 */
export const escapeHtml = (value) =>
  String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Construye el documento que se renderiza dentro del iframe aislado
 * @param {object} content html ya resuelto, texto alterno y permiso de remoto
 * @returns {string} documento html listo para el atributo srcDoc
 */
export const buildEmlDocument = ({ html, text, allowRemote }) => {
  const policy = allowRemote ? CSP_ALLOWED : CSP_BLOCKED;
  const body = html ? html : `<pre>${escapeHtml(text || '(El correo no tiene contenido)')}</pre>`;
  return (
    '<!DOCTYPE html><html><head><meta charset="utf-8">' +
    `<meta http-equiv="Content-Security-Policy" content="${policy}">` +
    '<base target="_blank">' +
    `<style>${FRAME_STYLE}</style></head><body>${body}</body></html>`
  );
};

/**
 * Reemplaza las referencias cid: del html por urls locales de las imagenes
 * @param {string} html cuerpo html del correo
 * @param {object} urls mapa de content-id a url local
 * @returns {string} html con las imagenes incrustadas resueltas
 */
export const resolveCidImages = (html, urls) =>
  String(html).replace(/(["'(])cid:([^"')\s]+)/gi, (match, prefix, contentId) => {
    const url = urls[decodeURIComponent(contentId)] || urls[contentId];
    return url ? `${prefix}${url}` : match;
  });

/**
 * Crea las urls locales de las imagenes incrustadas de un correo
 * @param {Array} attachments adjuntos del correo
 * @returns {object} mapa de content-id a url local
 */
export const createInlineUrls = (attachments) => {
  const urls = {};
  (attachments || [])
    .filter((item) => item.contentId !== '')
    .forEach((item) => {
      urls[item.contentId] = URL.createObjectURL(new Blob([item.bytes], { type: item.contentType }));
    });
  return urls;
};
